// DS1.10
import React, { useCallback, useContext } from 'react';
import c from 'classnames';
import { useReverseTheme } from '@wix/da-react-context/pkg/ThemeContext';
import SimpleTooltipContent from '../../SimpleTooltipContent';
import PopperPositionedPopover, {
  Props as PopperPositionedPopoverProps,
} from '../../../popper/PopperPositionedPopover';
import { usePopoverOnInteraction } from '../../../popper/hooks/usePopoverOnInteraction';
import PopoverWrapper from '../../../popper/PopoverWrapper';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';

import s from './SimpleTooltip.scss';

interface Props extends Omit<
  PopperPositionedPopoverProps,
  | 'renderPopover'
  | 'isVisible'
  | 'onReferenceClick'
  | 'onReferenceMouseEnter'
  | 'onReferenceMouseLeave'
> {
  /**
   * The main text of the tooltip.
   */
  text?: React.ReactNode;

  /**
   * Displays before the tooltip text. Consider putting an icon here.
   */
  prefix?: React.ReactNode;

  /**
   * Displays after the tooltip text. Consider putting an icon here.
   */
  postfix?: React.ReactNode;

  /**
   * Limits the width of the tooltip.
   */
  width?: number;

  /**
   * Force tooltip to be visible.
   */
  forceVisible?: boolean;

  /**
   * Provide a class name for the popover content wrapper
   */
  contentClassName?: string;

  /**
   * Provide a class name for the popover container
   */
  containerClassName?: string;

  /**
   * Provide a class name for the arrow
   */
  arrowClassname?: string;

  /**
   * Delay before showing tooltip
   */
  hoverDelay?: number;

  /**
   * If the popover handler should show on click or not - Default is true (dunno why???)
   */
  listenOnClicks?: boolean;

  /**
   * Callback to be called when the popover first mounts (this will happen after any hover delay)
   */
  onVisible?: () => void;

  stopPropagation?: boolean;
}

const SimpleTooltip: React.FC<Props> = ({
  text,
  children,
  prefix,
  postfix,
  width,
  forceVisible = false,
  className,
  contentClassName,
  arrowClassname,
  containerClassName,
  hoverDelay = 1,
  listenOnClicks = true,
  stopPropagation,
  onVisible,
  ...rest
}) => {
  const isMobile = useContext(MobileContext);
  const { isVisible, onClick, onMouseEnter, onMouseLeave, onClose } = usePopoverOnInteraction({
    listenOnClicks,
    listenOnHover: !isMobile,
    hoverDelay,
    onVisible,
  });
  const reverseTheme = useReverseTheme();

  const renderPopover: PopperPositionedPopoverProps['renderPopover'] = useCallback(
    popoverWrapperProps => (
      <PopoverWrapper
        className={containerClassName}
        contentClassName={c(s['popover-wrapper'], contentClassName)}
        arrowClassName={arrowClassname}
        width={width}
        onClickOutside={onClose}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        forceTheme={reverseTheme}
        {...popoverWrapperProps}
      >
        <SimpleTooltipContent prefix={prefix} postfix={postfix}>
          {text}
        </SimpleTooltipContent>
      </PopoverWrapper>
    ),
    [
      width,
      text,
      prefix,
      postfix,
      onMouseEnter,
      onMouseLeave,
      onClose,
      reverseTheme,
      contentClassName,
      containerClassName,
      arrowClassname,
    ]
  );

  if (!text) {
    return <>{children}</>;
  }

  return (
    <PopperPositionedPopover
      isVisible={isVisible || forceVisible}
      onReferenceClick={e => {
        onClick();
        if (stopPropagation) {
          e.stopPropagation();
          e.preventDefault();
        }
      }}
      onReferenceMouseEnter={onMouseEnter}
      onReferenceMouseLeave={onMouseLeave}
      className={c(s['root'], className)}
      renderPopover={renderPopover}
      {...rest}
    >
      {children}
    </PopperPositionedPopover>
  );
};
SimpleTooltip.displayName = 'SimpleTooltip';

export default SimpleTooltip;
